import React, { useState, useEffect } from "react";
import { FaEye, FaDownload, FaBuilding, FaMapMarkerAlt, FaPhone, FaEnvelope, FaGlobe, FaTimes, FaArrowLeft, FaImage, FaFile, FaCalendar, FaTag, FaCheck, FaTimes as FaTimesIcon } from "react-icons/fa";
import { useParams } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { getTheme } from '../../theme.ts';
import api from "../../services/api.ts";
import { useQuery } from "@tanstack/react-query";

// Local fallback component for broken images
const FallbackImage: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`${className} flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800`}>
    <FaImage className="text-3xl text-gray-400 dark:text-gray-500" />
  </div>
);

interface OrganizationDetails {
  id: string;
  email: string;
  role: string;
  profile: {
    organization_name: string;
    street: string;
    city: string;
    state: string;
    pincode: string;
    landmark: string;
    address: string;
    phone_number: string;
    is_verified: boolean;
    profile_picture: string;
    documents: string[];
    id: string;
  };
  verification_info: {
    id: string;
    organization: string;
    profile: string | null;
    requested_at: string;
    status: string;
    admin_comments: string | null;
  } | null;
  stats: {
    brand: number;
    campaign: number;
    total_request: number;
  };
  social_accounts: any[];
  subscription: any | null;
}

// Direct API fetch function
const fetchOrganizationDetails = async (organizationId: string) => {
  if (!organizationId) return null;
  const DB_URI = process.env.REACT_APP_DB_URI;
  const { data } = await api.get(`${DB_URI}/users/admin/${organizationId}/`);
  return data;
};

const ViewDocs = () => {
  const { themeName } = useTheme();
  const currentTheme = getTheme(themeName);
  const [imageError, setImageError] = useState(false);
  const { id } = useParams();

  // Direct useQuery instead of custom hook
  const { 
    data: orgDetails, 
    isLoading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ['organizationDetails', id],
    queryFn: () => fetchOrganizationDetails(id || ""),
    enabled: !!id,
    retry: 1
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'APPROVED':
        return {
          bg: `${currentTheme.success}15`,
          text: currentTheme.success,
          border: `${currentTheme.success}30`,
          icon: <FaCheck className="mr-1" size={12} />
        };
      case 'PENDING':
        return {
          bg: `${currentTheme.warning}15`,
          text: currentTheme.warning,
          border: `${currentTheme.warning}30`,
          icon: <FaCalendar className="mr-1" size={12} />
        };
      case 'REJECTED':
        return {
          bg: `${currentTheme.error}15`,
          text: currentTheme.error,
          border: `${currentTheme.error}30`,
          icon: <FaTimesIcon className="mr-1" size={12} />
        };
      default:
        return {
          bg: `${currentTheme.textSecondary}15`,
          text: currentTheme.textSecondary,
          border: `${currentTheme.textSecondary}30`,
          icon: null
        };
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: currentTheme.background }}>
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-10 h-10 bg-blue-500/20 rounded-full animate-pulse"></div>
            </div>
          </div>
          <p className="mt-6 text-lg font-medium" style={{ color: currentTheme.text }}>Loading Organization Profile...</p>
        </div>
      </div>
    );
  }

  if (error || !orgDetails) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: currentTheme.background }}>
        <div className="max-w-md w-full text-center p-8 rounded-2xl shadow-2xl" style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}>
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center shadow-xl">
            <FaTimesIcon className="text-4xl text-white" />
          </div>
          <h2 className="text-2xl font-bold mb-3" style={{ color: currentTheme.text }}>Error Loading Organization</h2>
          <p className="mb-8" style={{ color: currentTheme.textSecondary }}>
            {(error as any)?.message || 'Unable to load organization data'}
          </p>
          <button
            onClick={() => refetch()}
            className="px-8 py-3 rounded-xl font-medium transition-all hover:scale-105 shadow-lg mx-auto"
            style={{ backgroundColor: currentTheme.primary, color: currentTheme.inverseText }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const statusStyle = getStatusColor(orgDetails.verification_info?.status || '');

  return (
    <div className="min-h-screen p-4 md:p-8" style={{ backgroundColor: currentTheme.background }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Organization Profile
            </h1>
            <p className="text-sm flex items-center gap-2" style={{ color: currentTheme.textSecondary }}>
              <span>ID: {orgDetails.id}</span>
              <span className="w-1 h-1 rounded-full bg-gray-300"></span>
              <span>Role: {orgDetails.role}</span>
            </p>
          </div>
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all hover:scale-105 shadow-lg"
            style={{ backgroundColor: currentTheme.primary, color: currentTheme.inverseText }}
          >
            <FaArrowLeft size={16} />
            Back
          </button>
        </div>

        {/* Main Profile Card */}
        <div className="rounded-2xl shadow-2xl overflow-hidden mb-8 transition-all hover:shadow-3xl"
             style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}>
          
          {/* Cover Gradient */}
          <div className="h-32 bg-gradient-to-r from-blue-500 to-purple-600 relative">
            <div className="absolute -bottom-16 left-8">
              <div className="relative group">
                <div className="w-32 h-32 rounded-2xl border-4 overflow-hidden shadow-xl transition-transform group-hover:scale-105"
                     style={{ borderColor: currentTheme.surface }}>
                  {orgDetails.profile.profile_picture && !imageError ? (
                    <img
                      src={`${process.env.REACT_APP_PF}${orgDetails.profile.profile_picture}`}
                      alt={orgDetails.profile.organization_name}
                      className="w-full h-full object-cover"
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <FallbackImage className="w-full h-full" />
                  )}
                </div>
                {orgDetails.profile.is_verified && (
                  <div className="absolute -bottom-2 -right-2 bg-green-500 rounded-full p-1.5 border-2 border-white shadow-lg">
                    <FaCheck className="text-white" size={12} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Profile Info */}
          <div className="pt-20 p-8">
            <div className="flex flex-col lg:flex-row justify-between items-start gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <h2 className="text-3xl font-bold" style={{ color: currentTheme.text }}>
                    {orgDetails.profile.organization_name}
                  </h2>
                  {orgDetails.profile.is_verified && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      Verified
                    </span>
                  )}
                </div>
                
                {/* Email & Contact */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <InfoChip 
                    icon={<FaEnvelope />}
                    label="Email"
                    value={orgDetails.email}
                    theme={currentTheme}
                  />
                  <InfoChip 
                    icon={<FaPhone />}
                    label="Phone"
                    value={orgDetails.profile.phone_number || 'Not provided'}
                    theme={currentTheme}
                  />
                </div>
              </div>

              {/* Verification Status Card */}
              {orgDetails.verification_info && (
                <div className="lg:w-80 w-full">
                  <div className="p-5 rounded-xl transition-all hover:shadow-lg"
                       style={{ backgroundColor: statusStyle.bg, border: `1px solid ${statusStyle.border}` }}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold flex items-center gap-2" style={{ color: statusStyle.text }}>
                        {statusStyle.icon}
                        Verification Status
                      </h4>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span style={{ color: currentTheme.textSecondary }}>Status:</span>
                        <span className="font-medium" style={{ color: statusStyle.text }}>
                          {orgDetails.verification_info.status}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: currentTheme.textSecondary }}>Requested:</span>
                        <span style={{ color: currentTheme.text }}>
                          {formatDate(orgDetails.verification_info.requested_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Address */}
          <div className="lg:col-span-1 space-y-6">
            <InfoCard title="Address Details" icon={<FaMapMarkerAlt />} theme={currentTheme}>
              <div className="space-y-4">
                <InfoRow 
                  icon={<FaBuilding />}
                  label="Street"
                  value={orgDetails.profile.street}
                  theme={currentTheme}
                />
                <InfoRow 
                  icon={<FaMapMarkerAlt />}
                  label="City"
                  value={orgDetails.profile.city}
                  theme={currentTheme}
                />
                <InfoRow 
                  icon={<FaGlobe />}
                  label="State"
                  value={orgDetails.profile.state}
                  theme={currentTheme}
                />
                <InfoRow 
                  icon={<FaTag />}
                  label="Pincode"
                  value={orgDetails.profile.pincode}
                  theme={currentTheme}
                />
                {orgDetails.profile.landmark && (
                  <InfoRow 
                    icon={<FaBuilding />}
                    label="Landmark"
                    value={orgDetails.profile.landmark}
                    theme={currentTheme}
                  />
                )}
              </div>
            </InfoCard>
          </div>

          {/* Right Column - Stats & Documents */}
          <div className="lg:col-span-2 space-y-6">
            {/* Stats Cards */}
            {orgDetails.stats && (
              <InfoCard title="Statistics" icon={<FaEye />} theme={currentTheme}>
                <div className="grid grid-cols-3 gap-4">
                  <StatCard 
                    label="Brands"
                    value={orgDetails.stats.brand}
                    theme={currentTheme}
                  />
                  <StatCard 
                    label="Campaigns"
                    value={orgDetails.stats.campaign}
                    theme={currentTheme}
                  />
                  <StatCard 
                    label="Total Requests"
                    value={orgDetails.stats.total_request}
                    theme={currentTheme}
                  />
                </div>
              </InfoCard>
            )}

            {/* Documents Section */}
            {/* <InfoCard title="Documents" icon={<FaFile />} theme={currentTheme}>
              {orgDetails.profile.documents && orgDetails.profile.documents.length > 0 ? (
                <div className="space-y-3">
                  {orgDetails.profile.documents.map((docId, index) => (
                    <div
                      key={index}
                      className="group/doc relative rounded-xl p-4 transition-all duration-300 hover:shadow-lg hover:scale-[1.02]"
                      style={{ backgroundColor: currentTheme.card, border: `1px solid ${currentTheme.border}` }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-3 rounded-xl" style={{ backgroundColor: `${currentTheme.primary}15` }}>
                          <FaFile className="text-xl" style={{ color: currentTheme.primary }} />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-mono" style={{ color: currentTheme.text }}>
                            {docId}
                          </p>
                          <p className="text-xs mt-1" style={{ color: currentTheme.textSecondary }}>
                            Document ID
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-r from-blue-500/20 to-purple-500/20 flex items-center justify-center">
                    <FaFile className="text-3xl" style={{ color: currentTheme.primary }} />
                  </div>
                  <h4 className="text-lg font-medium mb-2" style={{ color: currentTheme.text }}>No Documents Found</h4>
                  <p style={{ color: currentTheme.textSecondary }}>This organization hasn't uploaded any documents yet.</p>
                </div>
              )}
            </InfoCard> */}

            {/* Full Address */}
            {orgDetails.profile.address && (
              <InfoCard title="Complete Address" icon={<FaMapMarkerAlt />} theme={currentTheme}>
                <div className="p-4 rounded-xl" style={{ backgroundColor: `${currentTheme.primary}5` }}>
                  <p className="text-lg font-mono break-all" style={{ color: currentTheme.text }}>
                    {orgDetails.profile.address}
                  </p>
                </div>
              </InfoCard>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper Components
interface InfoCardProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  theme: any;
}

const InfoCard: React.FC<InfoCardProps> = ({ title, icon, children, theme }) => (
  <div className="rounded-2xl shadow-xl overflow-hidden transition-all hover:shadow-2xl"
       style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}` }}>
    <div className="p-4 border-b" style={{ borderColor: theme.border, backgroundColor: `${theme.primary}15` }}>
      <h3 className="font-semibold flex items-center gap-2 text-lg" style={{ color: theme.primary }}>
        {icon}
        {title}
      </h3>
    </div>
    <div className="p-6">
      {children}
    </div>
  </div>
);

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  theme: any;
}

const InfoRow: React.FC<InfoRowProps> = ({ icon, label, value, theme }) => (
  <div className="flex items-start gap-3 group hover:translate-x-1 transition-transform">
    <div className="p-2 rounded-lg" style={{ backgroundColor: `${theme.primary}10`, color: theme.primary }}>
      {icon}
    </div>
    <div>
      <p className="text-xs" style={{ color: theme.textSecondary }}>{label}</p>
      <p className="text-sm font-medium" style={{ color: theme.text }}>{value}</p>
    </div>
  </div>
);

interface InfoChipProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  theme: any;
}

const InfoChip: React.FC<InfoChipProps> = ({ icon, label, value, theme }) => (
  <div className="flex items-center gap-2 p-3 rounded-xl transition-all hover:shadow-md"
       style={{ backgroundColor: `${theme.primary}5`, border: `1px solid ${theme.border}` }}>
    <div className="text-sm" style={{ color: theme.primary }}>{icon}</div>
    <div>
      <p className="text-xs" style={{ color: theme.textSecondary }}>{label}</p>
      <p className="text-sm font-medium truncate max-w-[200px]" style={{ color: theme.text }}>{value}</p>
    </div>
  </div>
);

interface StatCardProps {
  label: string;
  value: number;
  theme: any;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, theme }) => (
  <div className="text-center p-4 rounded-xl transition-all hover:scale-105 hover:shadow-lg"
       style={{ backgroundColor: `${theme.primary}5`, border: `1px solid ${theme.border}` }}>
    <p className="text-2xl font-bold" style={{ color: theme.primary }}>{value}</p>
    <p className="text-xs mt-1" style={{ color: theme.textSecondary }}>{label}</p>
  </div>
);

export default ViewDocs;